import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Building2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import CompagniaSezioni from "./CompagniaSezioni";
import CampiPraticaEditor from "./CampiPraticaEditor";

// Scheda di dettaglio compagnia (aperta dal nome in Anagrafiche, dal campo Compagnia in pratica
// o dal campo Ispettorato): preferenze "Campi visibili in pratica" + sottosezioni Ispettorati/Agenzie.
export default function CompagniaDettaglioDialog({ open, onOpenChange, rec, onSaved, defaultTab = "ispettorati" }) {
  const [campi, setCampi] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setCampi(Array.isArray(rec?.campi_pratica) ? rec.campi_pratica : null); }, [open, rec]);

  if (!rec) return null;

  const saveCampi = async () => {
    setSaving(true);
    try {
      const { data: doc } = await api.patch(`/anagrafiche/compagnie/${rec.id}`, { campi_pratica: campi || [] });
      toast.success("Campi visibili aggiornati");
      onSaved && onSaved(doc);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="compagnia-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Building2 className="h-5 w-5 text-amber-400" /> {rec.denominazione || rec.ragione_sociale || "Compagnia"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm" data-testid="compagnia-convenzione-card">
          <span className="text-slate-400">Convenzione risarcimento diretto: </span>
          {rec.convenzione_card === "aderisce" ? <span className="font-semibold text-emerald-400">Aderisce alla convenzione di risarcimento diretto</span>
            : rec.convenzione_card === "non_aderisce" ? <span className="font-semibold text-red-400">Non aderisce alla convenzione di risarcimento diretto</span>
            : <span className="text-slate-500">Non specificato</span>}
        </div>
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3" data-testid="compagnia-dati-readonly">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider font-mono text-slate-400">Dati compagnia</span>
            <span className="text-[11px] text-slate-500">Gestiti centralmente dall'amministratore · sola lettura</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            {[
              ["Nome", rec.denominazione || rec.ragione_sociale, "nome"],
              ["Telefono", rec.telefono, "telefono"],
              ["Email", rec.email, "email"],
              ["PEC", rec.pec, "pec"],
              ["Indirizzo", [[rec.indirizzo, rec.numero_civico].filter(Boolean).join(" "), [rec.cap, rec.comune, rec.provincia ? `(${rec.provincia})` : ""].filter(Boolean).join(" ")].filter(Boolean).join(", "), "indirizzo"],
              ["Partita IVA", rec.partita_iva, "partita_iva"],
            ].map(([l, v, k]) => (
              <div key={k} className="min-w-0">
                <div className="text-[11px] text-slate-500">{l}</div>
                <div className="text-slate-200 break-words" data-testid={`compagnia-ro-${k}`}>{v || "—"}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <CampiPraticaEditor value={campi} onChange={setCampi} testId="compagnia-campi" variant="dark" />
          <div className="flex justify-end">
            <Button type="button" size="sm" onClick={saveCampi} disabled={saving} data-testid="compagnia-campi-save"
              className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold h-9">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva campi visibili</>}
            </Button>
          </div>
        </div>
        <CompagniaSezioni rec={rec} onSaved={onSaved} defaultTab={defaultTab} />
      </DialogContent>
    </Dialog>
  );
}
